/**
 * ENG-8293: make `auth-tuple-change` restarts diagnosable.
 *
 * The manager restarts a session when its auth tuple changes, but the evidence
 * for WHICH part changed was written to exactly one place — the manager.log line
 * `Auth config changed for 'x' (old → new)` — which rotates away within about 75
 * minutes. The `agent.restart` audit row that survives carried
 * `reason: 'auth-tuple-change'`, a restatement of `source` that names nothing.
 *
 * The result: the 30 July wave (20 agents, 3 hosts, 4 orgs, inside 6.5 minutes)
 * is permanently unexplainable from surviving evidence. ENG-8276 is the same
 * complaint at the retention layer; this module fixes it at the durable layer so
 * the log line stops being load-bearing.
 *
 * This module turns the two tuple STRINGS the manager already holds into named
 * components plus a changed/unchanged verdict per component, so the audit row can
 * answer "what changed?" hours later without host log access.
 *
 * ── On secret material ──────────────────────────────────────────────────────
 * Every component of the tuple is non-secret BY CONSTRUCTION and is already
 * written verbatim to manager.log today:
 *   - `auth_mode`                     an enum (subscription | api_key | openrouter)
 *   - `anthropic_api_key_fingerprint` sha256(key) truncated to 8 hex
 *                                     (packages/api/src/lib/claude-pair-actions.ts)
 *   - `claude_account_fingerprint`    sha256(accountUuid:orgUuid) truncated to 12 hex
 *                                     (claude-account-fingerprint.ts)
 *   - `openrouter_fingerprint`        sha256(authToken) truncated to 16 hex, or the
 *                                     public model id when the API sent no
 *                                     fingerprint (host-runtime.ts)
 * No raw key, token, account uuid or org uuid is reachable from any of them, so
 * carrying the VALUES (not just the names) is safe — and it is what lets an
 * operator answer the question the names alone cannot: did twenty agents across
 * four orgs move to the SAME new value, i.e. was this one control-plane push
 * rather than twenty coincidental rotations?
 *
 * ── On audit-log scrubbing (this is load-bearing, do not "simplify" it) ──────
 * `recordAuditEvent` scrubs `extra` with SECRET_KEY_PATTERNS, which includes
 * /api[_-]?key/i, /token/i and /credential/i — matched against KEY NAMES. A shape
 * like `{ anthropic_api_key_fingerprint: 'a1b2c3d4' }` would therefore be written
 * as `<redacted: secret-shaped>` and this whole fix would land looking correct and
 * diagnose nothing. So component names ride as string VALUES under the neutral
 * keys `component` / `changed` / `from` / `to`, which no pattern matches. The
 * `auth-tuple-diff-audit-shape` test in packages/api asserts this end to end.
 */

/** A single named element of the auth tuple, with its before/after values. */
export interface AuthTupleComponentDiff {
  /** Component name, carried as a VALUE (see the scrubbing note above). */
  component: string;
  changed: boolean;
  /** Value in the tuple the session was launched with. */
  from: string;
  /** Value in the tuple observed on this poll. */
  to: string;
}

export interface AuthTupleDiff {
  /** Names of the components that differ. Empty only if the tuples are equal. */
  changed: string[];
  /** Names of the components that are identical across the two tuples. */
  unchanged: string[];
  /** Per-component detail, in stable component order. */
  components: AuthTupleComponentDiff[];
  /**
   * One-line human summary, safe for a log line and for the audit row's `reason`
   * (which is capped at 500 chars server-side). Never contains secret material.
   */
  summary: string;
}

/** Sentinel for "this component is absent from this tuple shape". */
const ABSENT = 'none';

/**
 * Stable ordering so a diff of the same two tuples always renders identically,
 * and so a shape change (api_key → openrouter) reads top-down rather than
 * scrambled by object key order.
 */
const COMPONENT_ORDER = [
  'auth_mode',
  'anthropic_api_key_fingerprint',
  'claude_account_fingerprint',
  'openrouter_fingerprint',
  // ENG-9483 slice 2: `<policyId>@<revision>` from modelPolicyAuthComponent.
  // Non-secret: a uuid the operator chose plus an integer they can see in the UI.
  'model_policy',
] as const;

/**
 * Split an auth tuple string back into its named components.
 *
 * The two shapes built in manager-worker.ts are:
 *   openrouter:  `openrouter:<fingerprint | model>`
 *   claude:      `<mode>:<apiKeyFingerprint | 'none'>[:<accountFingerprint>]`
 * either of which may carry an ENG-9483 policy suffix: `<shape>|policy=<id>@<rev>`.
 *
 * ── Why the policy rides as a NAMED suffix and not a fourth colon segment ────
 *
 * The claude branch below reads its components POSITIONALLY (`parts[0..2]`) and
 * drops anything further, and the openrouter branch takes everything after the
 * first colon as one value. So appending `:<policy>` to the claude shape parses
 * to a record IDENTICAL to the one without it — the manager would restart the
 * session (the tuple strings differ) while this function reported
 * `no component differs (tuple string re-formatted)`, i.e. an unexplained
 * restart wearing the label of a benign one. That is exactly the diagnosis gap
 * ENG-8293 exists to close, so a positional append would have quietly reopened
 * it. Appending to the openrouter shape is worse in a different way: it folds
 * into `openrouter_fingerprint` and reports a credential rotation that did not
 * happen. `|` is used because it cannot occur in either shape — the fingerprints
 * are hex and a model id may contain `/` and `:` but not `|`.
 *
 * The OpenRouter credential is taken as EVERYTHING after the first colon rather
 * than split further, because the fallback value is a model id, which legitimately
 * contains `/` and `:` (e.g. `openrouter/anthropic/claude-sonnet-4.5:beta`).
 * The Claude fingerprints are hex sha256 prefixes, so splitting on `:` there is
 * unambiguous.
 *
 * Exported for tests. Never throws: an unrecognisable tuple degrades to a single
 * `auth_mode` component holding the raw string, which still yields a truthful
 * "something changed" row rather than a crash on a restart path.
 */
export const POLICY_TUPLE_SEPARATOR = '|policy=';

export function parseAuthTuple(tuple: string): Record<string, string> {
  const full = typeof tuple === 'string' ? tuple : '';

  // Strip the policy suffix BEFORE either shape branch, so both shapes gain the
  // component and neither has its own parsing perturbed by it. lastIndexOf, not
  // indexOf: the separator cannot appear in the shapes we build, but a
  // degraded/unknown tuple is passed through verbatim under `auth_mode`, and
  // taking the LAST occurrence keeps the policy component correct if such a
  // string ever contained the separator itself.
  const sepAt = full.lastIndexOf(POLICY_TUPLE_SEPARATOR);
  const raw = sepAt === -1 ? full : full.slice(0, sepAt);
  const policy = sepAt === -1 ? null : full.slice(sepAt + POLICY_TUPLE_SEPARATOR.length) || ABSENT;
  // OMITTED, not recorded as the ABSENT sentinel, when the tuple carries no
  // policy. Emitting `model_policy: 'none'` unconditionally looks like the
  // careful choice and is the wrong one twice over: `diffAuthTuples` already
  // unions the two key sets and defaults a one-sided component to ABSENT, so
  // assignment and removal are visible EITHER way — and adding the key to every
  // parse changes the shape returned for every unpoliced tuple, which is what
  // the six pre-existing `parseAuthTuple` cases in auth-tuple-diff.test.ts
  // assert by deep equality. Omission keeps a fleet with no policies parsing
  // byte-for-byte as it did.
  const withPolicy = (r: Record<string, string>): Record<string, string> =>
    policy === null ? r : { ...r, model_policy: policy };

  if (raw.startsWith('openrouter:')) {
    return withPolicy({
      auth_mode: 'openrouter',
      openrouter_fingerprint: raw.slice('openrouter:'.length) || ABSENT,
    });
  }

  const parts = raw.split(':');
  if (parts.length < 2) {
    // Not a shape we build. Keep the raw value visible under auth_mode so the
    // diff still reports the change honestly instead of silently normalising it.
    return withPolicy({ auth_mode: raw || ABSENT });
  }

  return withPolicy({
    auth_mode: parts[0] || ABSENT,
    anthropic_api_key_fingerprint: parts[1] || ABSENT,
    // Omitted from the string when there is no signed-in account fingerprint
    // (api_key mode, or subscription mode before the first successful read).
    // Recorded as the ABSENT sentinel so "account appeared" / "account
    // disappeared" is itself visible as a change.
    claude_account_fingerprint: parts[2] || ABSENT,
  });
}

/**
 * Build the auth tuple the manager compares across polls.
 *
 * Extracted from manager-worker.ts by ENG-9483 so that the CONSTRUCTION is
 * unit-testable alongside the parsing. Measured, not assumed: with the tuple
 * assembled inline, mutating the manager to append the policy as a naive `:`
 * segment — the precise defect the named separator exists to prevent, which
 * respawns the agent while the audit row reads "no component differs" — failed
 * ZERO tests, because every test built its own tuple string. Construction and
 * parsing have to be the same pair of functions for either to mean anything.
 *
 * Keep this the ONLY producer. A second one is how the two sides drift.
 */
export function buildAuthTuple(input: {
  /** ENG-7152 OpenRouter fingerprint, else the public model id. Null ⇒ claude shape. */
  readonly openRouterCredential: string | null;
  readonly claudeAuthMode: string;
  readonly anthropicApiKeyFingerprint: string | null;
  readonly claudeAccountFingerprint: string | null;
  /** ENG-9483 `<policyId>@<revision>`, or null when no policy is assigned. */
  readonly modelPolicyComponent: string | null;
}): string {
  const {
    openRouterCredential,
    claudeAuthMode,
    anthropicApiKeyFingerprint,
    claudeAccountFingerprint,
    modelPolicyComponent,
  } = input;
  // Omitted entirely — not written as an empty marker — when there is no policy,
  // so an unpoliced fleet's tuple is byte-for-byte what it was before ENG-9483
  // and deploying this does not respawn every agent at once.
  const suffix = modelPolicyComponent
    ? `${POLICY_TUPLE_SEPARATOR}${modelPolicyComponent}`
    : '';
  if (openRouterCredential) return `openrouter:${openRouterCredential}${suffix}`;
  return `${claudeAuthMode}:${anthropicApiKeyFingerprint ?? ABSENT}${
    claudeAccountFingerprint ? `:${claudeAccountFingerprint}` : ''
  }${suffix}`;
}

/**
 * Diff the tuple a session was launched with against the tuple observed now.
 *
 * Components present in only one of the two shapes (e.g. `openrouter_fingerprint`
 * after a switch away from OpenRouter) are reported as changed, with the ABSENT
 * sentinel on the side that lacks them.
 */
export function diffAuthTuples(recorded: string, current: string): AuthTupleDiff {
  const before = parseAuthTuple(recorded);
  const after = parseAuthTuple(current);

  const names = [
    ...COMPONENT_ORDER.filter((n) => n in before || n in after),
    // Anything unexpected (a tuple shape a newer manager introduced) still gets
    // reported rather than dropped — this path must never hide a change.
    ...Object.keys({ ...before, ...after })
      .filter((n) => !(COMPONENT_ORDER as readonly string[]).includes(n))
      .sort(),
  ];

  const components: AuthTupleComponentDiff[] = names.map((component) => {
    const from = before[component] ?? ABSENT;
    const to = after[component] ?? ABSENT;
    return { component, changed: from !== to, from, to };
  });

  const changed = components.filter((c) => c.changed).map((c) => c.component);
  const unchanged = components.filter((c) => !c.changed).map((c) => c.component);

  const summary =
    changed.length === 0
      ? 'auth-tuple-change: no component differs (tuple string re-formatted)'
      : `auth-tuple-change: ${changed
          .map((name) => {
            const c = components.find((x) => x.component === name)!;
            return `${name} ${c.from}→${c.to}`;
          })
          .join(', ')}${unchanged.length > 0 ? ` (unchanged: ${unchanged.join(', ')})` : ''}`;

  return { changed, unchanged, components, summary };
}
